// Dashboard 2 — Message Performance. Rank toggle (All / Best / Worst) over a
// sortable table; each row is a message template's send→session funnel with the
// N/A rule applied to open/click rates.

import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import { Panel } from "../../../Components/admin/reporting/controls";
import { FilterChip } from "../../../Components/admin/reporting/primitives";
import { getEngagementMessages } from "../../../Services/adminEngagement";
import {
  ChannelBadge,
  Rate,
  ErrorBox,
  EmptyState,
  WindowBadge,
  fmtInt,
  fmtPctValue,
  useEngagementData,
} from "./engagementShared";

const RANKS = [
  [null, "All"],
  ["best", "Best"],
  ["worst", "Worst"],
];

// Numeric columns the table can sort by client-side.
const SORT_COLS = {
  total_sent: "Sent",
  delivered: "Delivered",
  sessions_started: "Sessions",
  conversion_pct: "Conv %",
};

const MessageTab = ({ range }) => {
  const [rank, setRank] = useState(null);
  const [sort, setSort] = useState({ key: "total_sent", dir: "desc" });

  const { data, loading, error } = useEngagementData(
    () => getEngagementMessages(range, { rank, limit: 200 }),
    [range, rank]
  );

  const rows = useMemo(() => {
    const list = [...(data?.messages || [])];
    // Backend already orders best/worst; only re-sort the unranked full table.
    if (rank) return list;
    const { key, dir } = sort;
    return list.sort((a, b) => {
      const av = a[key] ?? -Infinity;
      const bv = b[key] ?? -Infinity;
      return dir === "asc" ? av - bv : bv - av;
    });
  }, [data, sort, rank]);

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" }
    );

  const SortHead = ({ col }) => (
    <th className="py-2 px-3 text-right font-medium">
      <button
        type="button"
        onClick={() => !rank && toggleSort(col)}
        className={
          rank
            ? "cursor-default"
            : "inline-flex items-center gap-1 hover:text-foreground"
        }
      >
        {SORT_COLS[col]}
        {!rank && sort.key === col ? (
          sort.dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : null}
      </button>
    </th>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <WindowBadge window={data?.window} timezone={data?.timezone} />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Rank
          </span>
          {RANKS.map(([val, label]) => (
            <FilterChip
              key={label}
              active={rank === val}
              onClick={() => setRank(val)}
            >
              {label}
            </FilterChip>
          ))}
        </div>
      </div>

      <Panel
        title={
          rank === "best"
            ? "Top messages by session-start rate"
            : rank === "worst"
            ? "Underperforming messages (high volume, low conversion)"
            : "All messages"
        }
        right={
          data?.messages ? (
            <span className="text-xs text-muted-foreground">
              {fmtInt(data.messages.length)} messages
            </span>
          ) : null
        }
      >
        {loading && !data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorBox>{error}</ErrorBox>
        ) : rows.length === 0 ? (
          <EmptyState>No messages in this window.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Message</th>
                  <th className="py-2 px-3 font-medium">Channel</th>
                  <SortHead col="total_sent" />
                  <SortHead col="delivered" />
                  <th className="py-2 px-3 text-right font-medium">Open</th>
                  <th className="py-2 px-3 text-right font-medium">Click</th>
                  <SortHead col="sessions_started" />
                  <th className="py-2 px-3 text-right font-medium">Sess. rate</th>
                  <SortHead col="conversion_pct" />
                </tr>
              </thead>
              <tbody>
                {rows.map((m, i) => (
                  <tr
                    key={m.message_id || `${m.message_name}-${i}`}
                    className="border-b transition-colors hover:bg-slate-50"
                  >
                    <td className="py-2 pr-3 align-top">
                      <div className="font-medium">{m.message_name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {m.message_category || "Uncategorized"}
                        {m.message_id ? (
                          <span className="ml-1 font-mono opacity-70">
                            · {m.message_id}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-2 px-3 align-top">
                      <ChannelBadge channel={m.channel} />
                    </td>
                    <td className="py-2 px-3 text-right align-top tabular-nums">
                      {fmtInt(m.total_sent)}
                    </td>
                    <td className="py-2 px-3 text-right align-top tabular-nums text-muted-foreground">
                      {fmtInt(m.delivered)}
                    </td>
                    <td className="py-2 px-3 text-right align-top">
                      <Rate value={m.open_rate} available={m.open_rate_available} hint="open" />
                    </td>
                    <td className="py-2 px-3 text-right align-top">
                      <Rate value={m.click_rate} available={m.click_rate_available} hint="click" />
                    </td>
                    <td className="py-2 px-3 text-right align-top tabular-nums">
                      {fmtInt(m.sessions_started)}
                    </td>
                    <td className="py-2 px-3 text-right align-top">
                      <Rate
                        value={m.session_start_rate}
                        available={m.session_start_rate_available}
                      />
                    </td>
                    <td className="py-2 px-3 text-right align-top font-semibold tabular-nums">
                      {fmtPctValue(m.conversion_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default MessageTab;
